<template>
    <div class="account-visitor-card-content">
        <div v-if="userIdExists">
            <div class="account-visitor-card-content__header">
                <div class="account-visitor-card-content__header-top">
                    <div class="account-visitor-card-content__header-item">
                        <div
                            v-if="showAccTypeLabel"
                            v-pendo-tooltip="accountTypeLabelTooltip"
                            :class="['account-visitor-card-content__acc-type', labelTypeColorClass]">
                            {{ topLabelText }}
                        </div>
                        <pendo-loading-indicator
                            v-else-if="isMetadataLoading"
                            type="skeleton"
                            :skeleton-props="{ height: 20, width: 80 }" />
                        <div class="account-visitor-card-content__title-header">
                            <account-label
                                v-if="accountDomainValue && kind === 'account'"
                                class="account-visitor-card-content__logo"
                                :account="{ domain: validatedAccountDomain }"
                                show-icon
                                size="large" />
                            <div class="account-visitor-card-content__info">
                                <div class="account-visitor-card-content__name">
                                    <pendo-loading-indicator
                                        v-if="isNameLoading"
                                        type="skeleton"
                                        :skeleton-props="{
                                            height: 20,
                                            width: 250,
                                        }" />
                                    <span v-else>{{ displayedName }}</span>
                                </div>
                                <div
                                    v-if="kind === 'account'"
                                    class="account-visitor-card-content__domain">
                                    <a
                                        :href="`https://${validatedAccountDomain}`"
                                        target="_blank"
                                        @mouseenter="hoveringOnDomain = true"
                                        @mouseleave="hoveringOnDomain = false">{{ accountDomainValue }}</a>
                                    <pendo-icon
                                        v-if="hoveringOnDomain"
                                        type="external-link"
                                        size="12" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!isSalesforceSourced"
                        class="account-visitor-card-content__header-item">
                        <pendo-router-link
                            :to="userLink"
                            target="_blank"
                            class="account-visitor-card-content__link">
                            <pendo-icon
                                type="external-link"
                                stroke="var(--palette-gray-80)"
                                size="16" />
                        </pendo-router-link>
                    </div>
                </div>
                <div
                    v-if="showVisitorsAccount"
                    class="account-visitor-card-content__visitors-account-container">
                    <div v-pendo-loading="{ loading: !displayedVisitorAccountName, skeletonProps: { height: 40 } }">
                        <pendo-divider height="1px" />
                        <div class="account-visitor-card-content__visitors-account">
                            <account-label
                                v-if="accountDomainValue"
                                class="account-visitor-card-content__logo"
                                :account="{ domain: validatedAccountDomain }"
                                show-icon
                                size="large" />
                            <span class="account-visitor-card-content__name">
                                {{ displayedVisitorAccountName }}
                            </span>
                            <span
                                v-if="hasMultipleAccounts"
                                v-pendo-tooltip="{
                                    arrow: true,
                                    content: extraAccountsList,
                                    placement: 'right',
                                    container: 'div.pendo-popover__inner',
                                }"
                                class="account-visitor-card-content__extra-accounts"
                                @mouseenter="fetchExtraVisitorAccounts">
                                {{
                                    $t('accountVisitorCardContent.+{moreAccountsLength} more', {
                                        moreAccountsLength: userAccountIds.length - 1,
                                    })
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <pendo-divider height="1px" />
            <div class="account-visitor-card-content__body">
                <pendo-alert
                    v-if="showMetadataAlert"
                    type="info">
                    <span
                        v-if="!isAdmin"
                        class="account-visitor-card-content__no-metadata-mapped">
                        {{ metadataAlertNoAdminText }}
                    </span>
                    <i18n-t
                        v-if="isAdmin && kind === 'account'"
                        keypath="accountVisitorCardContent.noMetadataMapped.account.mainMessage"
                        tag="span"
                        class="account-visitor-card-content__no-metadata-mapped">
                        <template #link>
                            <pendo-router-link
                                :to="{ name: 'DataMappings', query: { level: kind } }"
                                target="_blank">
                                <span class="account-visitor-card-content__no-metadata-mapped-link">
                                    {{
                                        $t(
                                            'accountVisitorCardContent.metadataLinkText.configure your metadata in settings.',
                                        )
                                    }}
                                </span>
                            </pendo-router-link>
                        </template>
                    </i18n-t>
                    <i18n-t
                        v-else-if="isAdmin && kind === 'visitor'"
                        keypath="accountVisitorCardContent.noMetadataMapped.visitor.mainMessage"
                        tag="span"
                        class="account-visitor-card-content__no-metadata-mapped">
                        <template #link>
                            <pendo-router-link
                                :to="{ name: 'DataMappings', query: { level: kind } }"
                                target="_blank">
                                <span class="account-visitor-card-content__no-metadata-mapped-link">
                                    {{
                                        $t(
                                            'accountVisitorCardContent.metadataLinkText.configure your metadata in settings.',
                                        )
                                    }}
                                </span>
                            </pendo-router-link>
                        </template>
                    </i18n-t>
                </pendo-alert>
                <div v-if="isMetadataLoading">
                    <div
                        v-for="(item, index) in skeletonItems"
                        :key="index"
                        class="account-visitor-card-content__skeleton-wrapper">
                        <pendo-loading-indicator
                            type="skeleton"
                            :skeleton-props="{
                                width: item.width,
                                height: item.height,
                            }" />
                    </div>
                </div>
                <div v-else>
                    <div class="account-visitor-card-content__metadata-container">
                        <div>
                            <div
                                v-for="metadata in selectedMetadataFields"
                                :key="metadata.field"
                                class="account-visitor-card-content__row">
                                {{ metadata.label }}: {{ metadata.formattedValue }}
                            </div>
                        </div>
                        <div>
                            <pendo-icon-button
                                v-if="kind === 'account' && !hideManageMetadata"
                                tooltip="Manage metadata"
                                icon="edit"
                                theme="app"
                                @click="openManageMetadataModal" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-else-if="noResults"
            class="account-visitor-card-content__empty-state">
            <img
                src="@img/voc/hover-card-empty-state.svg"
                width="98px"
                height="93px">
        </div>
        <div
            v-else
            class="account-visitor-card-content__all-accounts">
            <div class="account-visitor-card-content__all-accounts-title">
                {{ kind === 'visitor' ? $t('All Visitors') : $t('All Accounts') }}
            </div>
            <pendo-divider />
            <div class="account-visitor-card-content__all-accounts-description">
                <p>
                    {{ allAccountsDescription }}
                </p>
                <i18n-t
                    v-if="kind === 'account'"
                    keypath="accountVisitorCardContent.adminAccountTypeSetup.mainMessage">
                    <template #link>
                        <pendo-router-link
                            :to="{ name: 'DataMappings', query: { level: 'account' } }"
                            target="_blank">
                            <i18n-t keypath="accountVisitorCardContent.settingsMetadataPath">
                                <template #separator>
                                    <span :aria-label="$t('and then')">></span>
                                </template>
                            </i18n-t>
                        </pendo-router-link>
                    </template>
                </i18n-t>
            </div>
        </div>
        <div class="account-visitor-card-content__metrics-container">
            <div
                v-for="metricBlock in formattedMetrics"
                :key="metricBlock.id"
                class="account-visitor-card-content__metrics"
                :style="{ flex: metricBlock.metrics.length }">
                <div class="account-visitor-card-content__metric-title">
                    {{ metricBlock.title }}
                </div>
                <div class="account-visitor-card-content__metric-block">
                    <div
                        v-for="(metric, index) in metricBlock.metrics"
                        :key="index"
                        class="account-visitor-card-content__metric">
                        <div class="account-visitor-card-content__metric-value-container">
                            <pendo-icon
                                v-if="metric?.icon"
                                size="15"
                                :type="metric.icon" />
                            <div class="account-visitor-card-content__metric-value">
                                <pendo-loading-indicator
                                    v-if="metric.value === null"
                                    type="skeleton"
                                    :skeleton-props="{ height: 20, width: 20 }" />
                                <span v-else>{{ metricValue(metric) }}</span>
                            </div>
                        </div>
                        <span
                            v-pendo-loading="{ loading: metric.label === null, skeletonProps: { height: 20 } }"
                            class="account-visitor-card-content__metric-label">
                            {{ metric.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <account-metadata-column-chooser
        v-if="showManageMetadataModal && !hideManageMetadata"
        :visible="showManageMetadataModal"
        :columns="displayedMetadataColumns"
        :saving="isSavingUserSettings"
        :is-loading-metadata="metadataLoading"
        :metadata-list="metadataList"
        @cancel="showManageMetadataModal = false"
        @close="showManageMetadataModal = false"
        @confirm="onConfirmMetadataChange" />
</template>

<script>
import AccountLabel from '@/common/account-label/account-label.vue';
import AccountMetricsHoverCardAggregation from '@/common/account-visitor-hover-card/account-visitor-card-content/aggregations/account-metrics-hover-card.aggregation';
import VisitorMetricsHoverCardAggregation from '@/common/account-visitor-hover-card/account-visitor-card-content/aggregations/visitor-metrics-hover-card.aggregation';
import TotalUsersCountAggregation from '@/common/account-visitor-hover-card/total-users-count.aggregation';
import { LOADING_STATES } from '@/constants';
import { getAvailableMetadataColumns } from '@/metadata/utils';
import { formatNumberWithCurrency } from '@/settings/data-mappings/utils/data-mappings.utils';
import { runAggregationOnce } from '@/utils/aggregation';
import { formatValueForSchema, prettyTime } from '@/utils/formatters';
import { getI18nKeyForMetadataDisplayName } from '@/utils/i18n/i18n-metadata';
import { getVisitorDetailUrl } from '@/visitor/utils';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { fetchUser } from '@/vuex/modules/visitors/visitors.utils';
import { sendAggregation } from '@pendo/agg-connect';
import {
    PendoAlert,
    PendoDivider,
    PendoIcon,
    PendoIconButton,
    PendoLoading,
    PendoLoadingIndicator,
    PendoTooltip,
} from '@pendo/components';
import { getAccountDetailUrl } from '@utils/accounts';
import moment, { DATE_FORMAT } from '@utils/moment-wrapper';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { mapActions, mapGetters, mapState } from 'vuex';

import { defineAsyncComponent } from 'vue';

export default {
    name: 'AccountVisitorCardContent',
    components: {
        AccountMetadataColumnChooser: defineAsyncComponent(() => import('./account-metadata-column-chooser.vue')),
        PendoDivider,
        PendoIcon,
        PendoLoadingIndicator,
        PendoRouterLink,
        PendoIconButton,
        PendoAlert,
        AccountLabel,
    },
    directives: {
        PendoLoading,
        PendoTooltip,
    },
    props: {
        kind: {
            type: String,
            default: 'account',
        },
        id: {
            type: String,
            required: true,
        },
        name: {
            type: String,
            default: '',
        },
        visitorsAccountId: {
            type: String,
            default: '',
        },
        visitorsAccountName: {
            type: String,
            default: '',
        },
        delay: {
            type: Number,
            default: 0,
        },
        presetUserObj: {
            type: [Object, null],
            default: null,
        },
        isSalesforceSourced: {
            type: Boolean,
            default: false,
        },
        hideManageMetadata: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['modal-visibility-change'],
    data () {
        return {
            hoveringOnDomain: false,
            isMetadataLoading: false,
            totalUsersCount: null,
            accountMetrics: {},
            visitorMetrics: {},
            userMap: {},
            showManageMetadataModal: false,
            selectedColumnOptions: [],
            metadataLoading: false,
            metadataList: [],
            userSettingsKey: 'accountVisitorHoverCard',
            isSavingUserSettings: false,
            userSettingLoaded: false,
            AGG_STREAM_ID: null,
        };
    },
    computed: {
        ...mapGetters({
            settingByName: 'userSettings/settingByName',
            isAdmin: 'auth/isAdmin',
            segmentIncludeAnonymous: 'globalFilters/segmentIncludeAnonymous',
            segmentBlacklist: 'globalFilters/segmentBlacklist',
        }),
        ...mapState({
            accountTypeProspects: (state) => state.subscription.settings.accountTypeProspects,
            accountTypeCustomers: (state) => state.subscription.settings.accountTypeCustomers,
            accountTypeChurned: (state) => state.subscription.settings.accountTypeChurned,
            accountType: (state) => state.subscription.settings.accountType,
            accountDomain: (state) => state.subscription.settings.accountDomain,
            accountDisplayName: (state) => state.subscription.settings.accountDisplayName,
            revenueMetadata: (state) => state.subscription.settings.revenue,
            currencyMetadata: (state) => state.subscription.settings.currency,
            visitorEmail: (state) => state.subscription.settings.visitorEmail,
        }),
        accountVisitorHoverCardUserSettings () {
            const settings = cloneDeep(get(this.settingByName(this.userSettingsKey, true), 'value', {}));

            return {
                ...settings,
                columns: settings?.columns?.map((setting) => {
                    return {
                        ...setting,
                        name: setting.name || this.getFieldDisplayName(setting.field),
                        label: setting.label || this.getFieldDisplayName(setting.field),
                    };
                }),
            };
        },
        displayedMetadataColumns () {
            const defaultColumns = this.getDefaultMetadataColumns();
            const userColumns = this.accountVisitorHoverCardUserSettings.columns || [];

            return this.kind === 'account' && userColumns.length > 0 ? userColumns : defaultColumns;
        },
        topLabelText () {
            return this.kind === 'visitor' ? this.visitorTypeLabel : this.accountTypeLabel;
        },
        isAccountProspect () {
            const accountTypeMetadataValue = this.getValueFromMetadata(this.accountType?.value);
            if (this.kind === 'account' && this.accountTypeProspects?.value) {
                const prospects = JSON.parse(this.accountTypeProspects.value);
                if (prospects.includes(accountTypeMetadataValue)) return true;

                return false;
            }

            return false;
        },
        isAccountCustomer () {
            const accountTypeMetadataValue = this.getValueFromMetadata(this.accountType?.value);
            if (this.kind === 'account' && this.accountTypeCustomers?.value) {
                const customers = JSON.parse(this.accountTypeCustomers.value);
                if (customers.includes(accountTypeMetadataValue)) return true;

                return false;
            }

            return false;
        },
        isAccountChurned () {
            const accountTypeMetadataValue = this.getValueFromMetadata(this.accountType?.value);
            if (this.kind === 'account' && this.accountTypeChurned?.value) {
                const churned = JSON.parse(this.accountTypeChurned.value);
                if (churned.includes(accountTypeMetadataValue)) return true;

                return false;
            }

            return false;
        },
        isAnnonymousVisitor () {
            return this.id?.startsWith('_PENDO_T_');
        },
        visitorTypeLabel () {
            return this.isAnnonymousVisitor ? this.$t('Anonymous visitor') : this.$t('Visitor');
        },
        accountTypeLabel () {
            if (this.isAccountProspect) return this.$t('Prospect');
            if (this.isAccountCustomer) return this.$t('Customer');
            if (this.isAccountChurned) return this.$t('Churned');
            if (this.accountTypeMetadataExists) return this.$t('Unknown account type');

            return null;
        },
        accountTypeMetadataExists () {
            return this.accountType && this.accountType?.value;
        },
        userLink () {
            return this.kind === 'account' ? getAccountDetailUrl(this.id) : getVisitorDetailUrl(this.id);
        },
        formattedFirstVisit () {
            return this.isValidDate(this.userFirstVisit) ? this.formatTimestamp(this.userFirstVisit) : null;
        },
        formattedLastVisit () {
            return this.isValidDate(this.userLastVisit) ? this.formatTimestamp(this.userLastVisit) : null;
        },
        skeletonItems () {
            return [
                { width: this.estimateWidth(this.id), height: 14 },
                ...(this.kind === 'visitor' ? [{ width: this.estimateWidth(this.visitorEmailName), height: 14 }] : []),
                { width: this.estimateWidth(this.formattedFirstVisit), height: 14 },
                { width: this.estimateWidth(this.formattedLastVisit), height: 14 },
            ];
        },
        accountDomainValue () {
            if (this.isMetadataLoading) return null;

            const domainPath = this.accountDomain?.value;

            if (this.showVisitorsAccount) {
                return get(this.currentVisitorsAccount, `metadata.${domainPath}`, null);
            }

            return this.getValueFromMetadata(domainPath);
        },
        isNameLoading () {
            return this.name ? false : this.isMetadataLoading;
        },
        displayedName () {
            if (this.name) {
                return this.name;
            }
            const path =
                this.kind === 'account'
                    ? `metadata.${this.accountDisplayName?.value}`
                    : `metadata.${this.visitorEmail?.value}`;
            const name = get(this.user, path, this.id);

            return name;
        },
        visitorEmailName () {
            const emailValue = this.getValueFromMetadata(this.visitorEmail?.value);

            return emailValue;
        },
        revenueAmount () {
            const revenueMetadataField = get(this.revenueMetadata, 'value', '').replace('account.', 'metadata.');
            const revAmount = get(this.user, revenueMetadataField, this.$t('N/A'));

            return this.isMetadataLoading ? null : formatNumberWithCurrency(revAmount, this.currencyMetadata?.value);
        },
        formattedMetrics () {
            if (this.noResults) return [];
            // all accounts selected
            if (this.id === '') {
                const metricLabel = this.kind === 'visitor' ? this.$t('Total visitors') : this.$t('Total accounts');

                return [
                    {
                        id: `total${capitalize(this.kind)}s`,
                        title: '',
                        metrics: [
                            {
                                label: metricLabel,
                                icon: 'user',
                                value: this.totalUsersCount,
                            },
                        ],
                    },
                ];
            }

            if (this.kind === 'visitor') {
                return this.formattedVisitorMetrics;
            }

            return this.formattedAccountMetrics;
        },
        formattedVisitorMetrics () {
            return [
                {
                    id: 'last30Days',
                    title: this.$t('Last 30 days'),
                    metrics: Object.values(this.visitorMetrics),
                },
            ];
        },
        formattedAccountMetrics () {
            return [
                {
                    id: 'last30Days',
                    title: this.$t('Last 30 days'),
                    metrics: Object.values(this.accountMetrics),
                },
                this.revenueMetadata && {
                    id: 'revenue',
                    title: this.$t('Revenue'),
                    metrics: [this.accountRevenueMetrics],
                },
            ].filter(Boolean);
        },
        user () {
            return this.userMap[this.id]?.user;
        },
        userAccountIds () {
            return this.kind === 'visitor' ? this.userMap[this.id]?.user?.accountIds : null;
        },
        userFirstVisit () {
            return this.userMap[this.id]?.firstVisit;
        },
        userLastVisit () {
            return this.userMap[this.id]?.lastVisit;
        },
        accountRevenueMetrics () {
            return {
                label: this.revenueMetadata?.label || null,
                value: this.revenueAmount,
            };
        },
        showVisitorsAccount () {
            return this.kind === 'visitor' && this.displayedVisitorAccountId;
        },
        currentVisitorsAccount () {
            return this.userMap[this.id]?.visitorsAccount;
        },
        userIdExists () {
            return this.id !== '' && this.id !== 'NO_RESULTS';
        },
        accountTypeLabelTooltip () {
            return (
                this.kind === 'account' &&
                this.accountTypeMetadataExists && {
                    content: this.$t(
                        'accountVisitorCardContent.Administrators can set up this mapping in Metadata under Settings.',
                    ),
                    arrow: true,
                    visible: false,
                }
            );
        },
        extraAccountsList () {
            return (
                this.userMap[this.id]?.extraAccounts
                    ?.map((account) => {
                        const accountName = get(account, `metadata.${this.accountDisplayName?.value}`, account.id);

                        return accountName || account.id;
                    })
                    .join('\n') || this.$t('Loading accounts...')
            );
        },
        displayedVisitorAccountId () {
            if (this.visitorsAccountId) return this.visitorsAccountId;
            if (this.userAccountIds?.length === 0) return null;

            return this.userAccountIds ? this.userAccountIds[this.userAccountIds?.length - 1] : null;
        },
        displayedVisitorAccountName () {
            if (this.visitorsAccountName) return this.visitorsAccountName;

            const accountName = get(
                this.currentVisitorsAccount,
                `metadata.${this.accountDisplayName?.value}`,
                this.currentVisitorsAccount?.id,
            );

            return accountName || this.currentVisitorsAccount?.id;
        },
        hasMultipleAccounts () {
            return this.userAccountIds?.length > 1;
        },
        aggSourceName () {
            return `${this.kind}s`;
        },
        noResults () {
            return this.id === 'NO_RESULTS';
        },
        selectedMetadataFields () {
            return this.displayedMetadataColumns
                .filter((column) => column && column.field)
                .map(({ field, name, label, schema }) => {
                    const value = this.getMetadataValue(field);
                    const formattedValue = this.formatMetadataValue(value, schema);
                    const truncatedValue = this.truncateValue(formattedValue);

                    return {
                        field,
                        name: name || this.getFieldDisplayName(field),
                        label: label || this.getFieldDisplayName(field),
                        formattedValue: truncatedValue,
                        originalValue: formattedValue,
                    };
                });
        },
        labelTypeColorClass () {
            const grayConditions = this.isAnnonymousVisitor || this.isAccountChurned || this.accountTypeMetadataExists;

            // eslint-disable-next-line internal-rules/no-hardcoded-colors
            return grayConditions ? 'account-visitor-card-content__acc-type--gray' : '';
        },
        showAccTypeLabel () {
            return !this.isMetadataLoading && this.topLabelText && this.id;
        },
        metadataAlertNoAdminText () {
            return this.kind === 'account'
                ? this.$t(
                    'accountVisitorCardContent.noMetadataMapped.To view more account details, ask your admin to configure metadata.',
                )
                : this.$t(
                    'accountVisitorCardContent.noMetadataMapped.To view more visitor details, ask your admin to configure metadata.',
                );
        },
        showMetadataAlert () {
            return !this.hasMappedMetadata && !this.isMetadataLoading && this.userIdExists;
        },
        currencyMetadataCheck () {
            return this.hasMetadataValue('currencyMetadata') && this.hasMetadataValue('revenueMetadata') ? {} : null;
        },
        hasMappedMetadata () {
            if (this.kind === 'account') {
                const accountChecks = ['accountType', 'accountDomain', 'accountDisplayName'];

                return accountChecks.some((field) => this.hasMetadataValue(field)) || !!this.currencyMetadataCheck;
            }
            if (this.kind === 'visitor') {
                const visitorChecks = ['visitorEmail'];

                return visitorChecks.some((field) => this.hasMetadataValue(field));
            }

            return false;
        },
        allAccountsDescription () {
            return this.kind === 'visitor'
                ? this.$t(
                    'accountVisitorCardContent.Includes all your visitors from paying accounts, prospects, anonymous and churned accounts.',
                )
                : this.$t(
                    'accountVisitorCardContent.Includes all your paying accounts, prospects, anonymous and churned accounts.',
                );
        },
        validatedAccountDomain () {
            try {
                // Ensure URL has a protocol to make URL parsing work
                let tempUrl = this.accountDomainValue.trim();
                if (!tempUrl.startsWith('http://') && !tempUrl.startsWith('https://')) {
                    tempUrl = `http://${tempUrl}`;
                }

                const parsed = new URL(tempUrl);

                return parsed.hostname;
            } catch {
                return this.accountDomainValue;
            }
        },
    },
    watch: {
        isSavingUserSettings: {
            handler (newVal, oldVal) {
                if (!newVal && oldVal && this.showManageMetadataModal) {
                    this.showManageMetadataModal = false;
                }
            },
        },
        showManageMetadataModal (newVal) {
            this.$emit('modal-visibility-change', newVal);
        },
    },
    created () {
        this.accountMetrics = {
            daysActive: {
                icon: 'zap',
                label: this.$t('Days active'),
                value: null,
            },
            uniqueVisitors: {
                icon: 'user',
                label: this.$t('Unique visitors'),
                value: null,
            },
        };

        this.visitorMetrics = {
            daysActive: {
                icon: 'zap',
                label: this.$t('Days active'),
                value: null,
            },
            avgDailyTime: {
                icon: 'clock',
                label: this.$t('Avg daily time'),
                value: null,
            },
            totalTime: {
                icon: 'visitor-timeline-activity',
                label: this.$t('Total time'),
                value: null,
            },
        };

        this.AGG_STREAM_ID = '3898ce0d-e752-4132-ba00-afc9c435d33a';
    },
    methods: {
        ...mapActions({
            loadSetting: 'subscription/loadSetting',
            updateUserSetting: 'userSettings/update',
            loadUserSetting: 'userSettings/loadOne',
        }),
        async loadMetadataForColumnChooser () {
            if (this.metadataList.length > 0) return;

            this.metadataLoading = true;
            try {
                const [metadataOptions] = await Promise.all([getAvailableMetadataColumns()]);
                this.metadataList = metadataOptions.filter((item) => item.kind === 'account');
            } catch (error) {
                console.error('Error loading metadata options:', error);
            } finally {
                this.metadataLoading = false;
            }
        },
        formatTimestamp (timeStamp) {
            return moment(timeStamp).format(DATE_FORMAT.short);
        },
        estimateWidth (text) {
            const baseWidth = 35;
            const charWidth = 8;

            return text ? Math.max(baseWidth, text.length * charWidth) : baseWidth;
        },
        /**
         * @public
         * handle when content appears
         */
        async showHandle () {
            if (!this.userSettingLoaded) {
                await this.loadUserSetting({ name: this.userSettingsKey, namespaced: true });
                this.userSettingLoaded = true;
            }
            if (this.id === null || this.id === undefined || this.isMetadataLoading || this.noResults) return;

            this.accountMetrics.daysActive.value = null;
            this.accountMetrics.uniqueVisitors.value = null;
            this.visitorMetrics.daysActive.value = null;
            this.visitorMetrics.avgDailyTime.value = null;
            this.visitorMetrics.totalTime.value = null;
            if (!this.userMap[this.id]) {
                this.isMetadataLoading = true;
            }
            if (this.delay) {
                const idCopy = this.id;
                await new Promise((resolve) => setTimeout(resolve, this.delay));
                if (idCopy !== this.id) {
                    return;
                }
            }

            //  id is an empty string when all accounts is selected
            if (this.id === '') {
                this.fetchTotalUsersCount();

                return;
            }

            try {
                this.fetchMetricData();
                if (this.userMap[this.id]) return;
                if (this.presetUserObj) {
                    this.fetchUserInfo();

                    return;
                }
                const accountSettingsToFetch = [
                    'accountTypeProspects',
                    'accountTypeCustomers',
                    'accountTypeChurned',
                    'accountType',
                    'accountDomain',
                    'accountDisplayName',
                    'revenue',
                    'currency',
                ];
                const visitorSettingsToFetch = ['visitorEmail', 'accountDomain', 'accountDisplayName'];
                const settingsToFetch = this.kind === 'account' ? accountSettingsToFetch : visitorSettingsToFetch;
                await Promise.all([
                    this.fetchUserInfo(),
                    ...settingsToFetch.map((setting) =>
                        this.loadSetting({ setting, retrieveMetadata: setting === 'revenue' }),
                    ),
                ]);
            } catch (error) {
                console.error('Error loading account data:', error);
            } finally {
                this.isMetadataLoading = false;
            }
        },
        isValidDate (date) {
            return date && !isNaN(new Date(date).getTime());
        },
        getValueFromMetadata (path) {
            const val = get(this.user, `metadata.${path}`, null);

            return val;
        },
        async fetchUserInfo (refetch = false) {
            if (this.userMap[this.id] && !refetch) return;

            try {
                if (this.presetUserObj) {
                    this.userMap[this.id] = {
                        user: this.presetUserObj,
                        lastVisit: this.presetUserObj?.metadata?.auto?.lastvisit,
                        firstVisit: this.presetUserObj?.metadata?.auto?.firstvisit,
                    };

                    return;
                }
                const idCopy = this.id;
                const fetchedData = await fetchUser(this.kind, this.id);
                const userData = fetchedData?.data || fetchedData;
                // only set the account if the accountId is the same one at the time of fetching
                if (idCopy === this.id) {
                    this.userMap[this.id] = {
                        user: userData,
                        lastVisit: userData?.metadata?.auto?.lastvisit,
                        firstVisit: userData?.metadata?.auto?.firstvisit,
                    };
                }
                if (this.showVisitorsAccount) {
                    this.getVisitorAccount();
                }
            } catch (error) {
                throw error;
            }
        },
        async getVisitorAccount () {
            if (!this.userMap[this.id]) return;
            const idCopy = this.displayedVisitorAccountId;
            const visitorsAccount = await fetchUser('account', this.displayedVisitorAccountId);
            if (idCopy === this.displayedVisitorAccountId) {
                this.userMap[this.id].visitorsAccount = visitorsAccount.data;
            }
        },
        /**
         * @public
         * set the fetching metadata state
         */
        setFetchingMetadata (fetching) {
            this.isMetadataLoading = fetching;
        },
        async fetchMetricData () {
            try {
                const idCopy = this.id;
                this.accountMetrics.daysActive.value = null;
                this.accountMetrics.uniqueVisitors.value = null;
                this.visitorMetrics.daysActive.value = null;
                this.visitorMetrics.avgDailyTime.value = null;
                this.visitorMetrics.totalTime.value = null;
                const thirtyDaysAgo = moment({ ignoreTimezone: true })
                    .subtract(29, 'days')
                    .startOf('day');
                const thirtyDaysAgoSpec = {
                    count: 30,
                    period: 'dayRange',
                    first: thirtyDaysAgo.valueOf(),
                };

                if (this.kind === 'account') {
                    const sendAccountMetricsHoverCardAggResponse = sendAggregation(
                        AccountMetricsHoverCardAggregation,
                        this.AGG_STREAM_ID,
                        {
                            accountId: this.id,
                            timeSeries: thirtyDaysAgoSpec,
                            blacklist: this.segmentBlacklist,
                            includeAnonymous: this.segmentIncludeAnonymous,
                        },
                    );

                    if (sendAccountMetricsHoverCardAggResponse.subscribe) {
                        sendAccountMetricsHoverCardAggResponse.subscribe(({ status, value }) => {
                            if (status === LOADING_STATES.RESOLVED && idCopy === this.id) {
                                this.accountMetrics.daysActive.value = value[0].daysActive;
                                this.accountMetrics.uniqueVisitors.value = value[0].numVisitors;
                            } else if (status === LOADING_STATES.REJECTED) {
                                this.accountMetrics.daysActive.value = '--';
                                this.accountMetrics.uniqueVisitors.value = '--';
                            }
                        });
                    }
                } else {
                    const sendVisitorMetricsHoverCardAggResponse = sendAggregation(
                        VisitorMetricsHoverCardAggregation,
                        this.AGG_STREAM_ID,
                        {
                            visitorId: this.id,
                            timeSeries: thirtyDaysAgoSpec,
                        },
                    );

                    if (sendVisitorMetricsHoverCardAggResponse.subscribe) {
                        sendVisitorMetricsHoverCardAggResponse.subscribe(({ status, value }) => {
                            if (status === LOADING_STATES.RESOLVED && idCopy === this.id) {
                                this.visitorMetrics.daysActive.value = value[0].daysActive;
                                this.visitorMetrics.avgDailyTime.value = prettyTime(value[0].avgTime, 'short', 2, true);
                                this.visitorMetrics.totalTime.value = prettyTime(value[0].totalTime, 'short', 2, true);
                            } else if (status === LOADING_STATES.REJECTED) {
                                this.visitorMetrics.daysActive.value = '--';
                                this.visitorMetrics.avgDailyTime.value = '--';
                                this.visitorMetrics.totalTime.value = '--';
                            }
                        });
                    }
                }
            } catch {
                this.accountMetrics.daysActive.value = '--';
                this.accountMetrics.uniqueVisitors.value = '--';
                this.visitorMetrics.daysActive.value = '--';
                this.visitorMetrics.avgDailyTime.value = '--';
                this.visitorMetrics.totalTime.value = '--';
            }
        },
        metricValue (metric) {
            return metric.value === null ? '--' : metric.value;
        },
        async fetchTotalUsersCount () {
            try {
                if (this.totalUsersCount) return;

                let additionalSourceCriteria = {};
                if (this.kind === 'account') {
                    additionalSourceCriteria = {
                        parentAccounts: true,
                        subAccounts: true,
                    };
                }

                const totalUsersCount = await runAggregationOnce(TotalUsersCountAggregation, {
                    aggSourceName: this.aggSourceName,
                    additionalSourceCriteria,
                });
                this.totalUsersCount = totalUsersCount[0].count;
            } catch (error) {
                console.error('Error fetching total accounts count:', error);
            }
        },
        async fetchExtraVisitorAccounts () {
            try {
                if (this.userMap[this.id].extraAccounts) return;
                const displayedAccountId = this.displayedVisitorAccountId;
                const otherAccounts = this.userAccountIds.filter((accountId) => accountId !== displayedAccountId);
                const otherAccountsData = await Promise.all(
                    otherAccounts.map((accountId) => fetchUser('account', accountId)),
                );
                this.userMap[this.id].extraAccounts = otherAccountsData.map((account) => account.data);
            } catch (error) {
                console.error('Error fetching extra visitor accounts:', error);
            }
        },
        getDefaultMetadataColumns () {
            return [
                {
                    field: 'auto.id',
                    name: this.getFieldDisplayName('auto.id'),
                    label: this.getFieldDisplayName('auto.id'),
                    schema: 'string',
                },
                ...(this.kind === 'visitor' && this.visitorEmailName
                    ? [
                        {
                            field: 'visitorEmail',
                            name: this.getFieldDisplayName('visitorEmail'),
                            label: this.getFieldDisplayName('visitorEmail'),
                            schema: 'string',
                        },
                    ]
                    : []),
                {
                    field: 'auto.firstvisit',
                    name: this.getFieldDisplayName('auto.firstvisit'),
                    label: this.getFieldDisplayName('auto.firstvisit'),
                    schema: 'time',
                },
                {
                    field: 'auto.lastvisit',
                    name: this.getFieldDisplayName('auto.lastvisit'),
                    label: this.getFieldDisplayName('auto.lastvisit'),
                    schema: 'time',
                },
            ];
        },
        onConfirmMetadataChange (selectedColumns) {
            const accountPrefix = 'account.';

            const config = cloneDeep(this.accountVisitorHoverCardUserSettings);
            config.columns = selectedColumns.map((column) => {
                let { field } = column;
                if (field.startsWith(accountPrefix)) {
                    field = field.slice(accountPrefix.length);
                }

                return {
                    name: column.name,
                    label: column.label,
                    field,
                    schema: column.schema,
                    sortable: column.sortable,
                };
            });
            this.updateUserSettingConfig(config);
        },
        async updateUserSettingConfig (config) {
            this.isSavingUserSettings = true;
            try {
                await this.updateUserSetting({
                    name: this.userSettingsKey,
                    namespaced: true,
                    value: config,
                });
                this.selectedColumnOptions = config.columns;
            } catch (error) {
                console.error('Error saving user settings:', error);
            } finally {
                this.isSavingUserSettings = false;
            }
        },
        getMetadataValue (field) {
            if (!field || typeof field !== 'string') {
                return null;
            }

            if (field === 'visitorEmail') {
                return this.visitorEmailName;
            }

            const value = this.getValueFromMetadata(field);

            return value;
        },
        getFieldDisplayName (field) {
            const fieldMap = {
                'auto.id': getI18nKeyForMetadataDisplayName('auto', 'id', this.kind),
                'auto.firstvisit': getI18nKeyForMetadataDisplayName('auto', 'firstvisit', this.kind),
                'auto.lastvisit': getI18nKeyForMetadataDisplayName('auto', 'lastvisit', this.kind),
                'visitorEmail': this.$t('Email'),
            };

            return fieldMap[field] || field.split('.').pop();
        },
        formatMetadataValue (value, schema) {
            if (value === null || value === undefined) {
                return '--';
            }

            try {
                return formatValueForSchema(value, schema, 'short') || '--';
            } catch (error) {
                console.error('Error formatting metadata value:', error);

                return '--';
            }
        },
        truncateValue (value, maxLength = 40) {
            if (!value || typeof value !== 'string') {
                return value;
            }

            if (value.length <= maxLength) {
                return value;
            }

            return `${value.substring(0, maxLength - 3)}...`;
        },
        async openManageMetadataModal () {
            this.showManageMetadataModal = true;
            await this.loadMetadataForColumnChooser();
            if (!this.selectedColumnOptions.length) {
                this.selectedColumnOptions = this.accountVisitorHoverCardUserSettings.columns || [];
            }
        },
        hasMetadataValue (fieldName) {
            return !!(this[fieldName] && this[fieldName].value);
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';
.account-visitor-card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    &__header {
        display: flex;
        flex-direction: column;
    }

    &__title-header {
        line-height: 150%;
        display: flex;
        align-items: flex-start;
        gap: 6px;
        margin-bottom: 12px;
    }

    &__logo {
        flex-shrink: 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 4px;
    }
    &__visitor-logo {
        flex-shrink: 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }
    &__body {
        margin-bottom: 12px;
        color: var(--palette-gray-70);
        font-size: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
    }

    &__top {
        display: flex;
        justify-content: space-between;
    }

    &__acc-type {
        display: flex;
        padding: 4px 8px;
        justify-content: center;
        align-items: center;
        gap: 4px;
        border-radius: 3px;
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 10px;
        border: 1px solid var(--data-vis-orange-vis-orange-30);
        background: var(--data-vis-orange-vis-orange-30);
        width: fit-content;
    }

    &__acc-type--gray {
        border: 1px solid var(--palette-gray-40);
        background: var(--palette-gray-40);
        color: var(--palette-gray-100);
    }

    &__link {
        display: flex;
        margin-left: auto;
    }

    &__skeleton-wrapper {
        margin-bottom: 2px;
    }

    &__info {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    &__name {
        font-size: 14px;
        font-weight: 600;
        color: var(--palette-gray-100);
        padding-top: 1px;
        line-height: 150%;
        word-break: break-word;
    }

    &__domain {
        font-size: 12px;
        font-weight: 600;
        color: var(--palette-teal-70);
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
    }
    &__metrics-container {
        display: flex;
        flex-direction: row;
        gap: 5px;
        overflow: hidden;
    }
    &__metric-block {
        display: flex;
        flex-direction: row;
        justify-content: center;
        border: 1px solid var(--palette-gray-40);
        width: 100%;
        height: 100%;
        border-radius: 5px;
        background-color: var(--palette-gray-10);
    }
    &__metric {
        padding: 6px 4px;
        width: 100%;
        border-right: 1px solid var(--palette-gray-40);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
    }
    &__metric:last-child {
        border-right: none;
    }
    &__metric-value-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 2px;
        height: 20px;
    }
    &__metric-label {
        font-size: 10.5px;
        color: var(--palette-gray-70);
        min-height: 15px;
        min-width: 60px;
        text-align: center;
        word-break: break-word;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
    &__metric-value {
        font-size: 15px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 20px;
        min-width: 15px;
        white-space: nowrap;
    }
    &__metric-title {
        font-size: 11px;
        color: var(--palette-gray-70);
        margin-bottom: 5px;
        line-height: 150%;
    }
    &__row {
        line-height: 150%;
    }
    &__all-accounts-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--palette-gray-100);
        line-height: 150%;
    }
    &__all-accounts-description {
        color: var(--palette-gray-70);
        font-size: 12.64px;
        line-height: 150%;
    }
    &__metrics {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        width: 100%;
        overflow: hidden;
    }
    &__all-accounts {
        width: 300px;
        min-height: 256px;
    }
    &__visitors-account {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 4px;
        margin: 8px 0;
    }
    &__extra-accounts {
        color: var(--palette-teal-70);
        font-weight: 600;
        margin-bottom: 0px;
        cursor: default;
    }
    &__visitors-account-container {
        position: relative;
        min-height: 40px;
    }
    &__header-top {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 4px;
    }
    &__header-item {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }
    &__empty-state {
        min-height: 346px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    &__metadata-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }
    &__no-metadata-mapped {
        font-size: 12.642px;
        white-space: normal;
        word-break: break-word;
    }
    &__no-metadata-mapped-link {
        font-weight: 600;
        text-decoration: underline;
    }
    :deep(.pendo-alert) {
        padding: 8px;
        gap: 0px;
    }
}
</style>
